import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { FieldsService } from 'src/app/services/fields.service';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ReportDetailsDialogComponent } from '../report-details-dialog/report-details-dialog.component';
import { WeekCalculationService } from 'src/app/services/week-calculation.service';
import { LoggerService } from '../../../services/logger.service';

@Component({
  selector: 'app-ami-reports',
  templateUrl: './ami-reports.component.html',
  styleUrls: ['./ami-reports.component.css']
})
export class AmiReportsComponent implements OnInit {
  years: number[] = [];
  currentYear: number = new Date().getFullYear();
  currentWeek: number = this.getCurrentWeek();
  weeksData: { [year: number]: any[] } = {};
  submittedReports: { [key: string]: any[] } = {};
  rejectedReports: { [key: string]: any[] } = {};
  reports: any[] = [];
  amiReports: { [key: string]: any[] } = {}; // AMI Reports data by year
  expandedYears: Set<number> = new Set();
  filterYear: number | null = null;

  constructor(
    private fieldsService: FieldsService,
    private drupalRESTService: DrupalRESTService,
    private dialog: MatDialog,
    private weekCalculationService: WeekCalculationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Determine the correct year based on current date and week
    const now = new Date();
    this.currentYear = this.fieldsService.getCorrectYearForWeek(now);
    this.currentWeek = this.fieldsService.getCurrentWeek();

    this.generateYears();
    this.loadWeeksData();
    this.loadReports();
    this.loadAMIReports();
    // Auto-expand current year by default
    this.expandedYears.add(this.currentYear);
  }

  loadAMIReports(): void {
    // Load AMI reports for the current year to start
    this.getAMIReports(null, this.currentYear.toString()).subscribe(
      data => {
        if (data && Array.isArray(data)) {
          // Group reports by year
          data.forEach(report => {
            // If report doesn't have week_number or week_year, calculate them
            // This happens with miscellaneous reports that only have date_started
            if (!report.week_number || !report.week_year) {
              if (report.date_started) {
                const weekInfo = this.weekCalculationService.calculateWeekInfo(report.date_started);
                report.week_number = weekInfo.weekNumber;
                report.week_year = weekInfo.year;
                LoggerService.log(`Calculated week info for ${report.type} report:`, weekInfo);
              } else {
                LoggerService.warn('Report missing both week info and date_started:', report);
                return;
              }
            }

            const year = report.week_year;
            if (!year) {
              LoggerService.warn('Report missing week_year:', report);
              return;
            }
            if (!this.amiReports[year]) {
              this.amiReports[year] = [];
            }
            this.amiReports[year].push(report);
          });
          LoggerService.log('Loaded AMI Reports:', this.amiReports);
        }
      },
      error => {
        // Handle error silently - likely no data for this year yet
        LoggerService.log(`No AMI reports available for year ${this.currentYear}`);
        this.amiReports[this.currentYear] = [];
      }
    );
  }

  getWeekEndingDate(year: number, weekNum: number): string {
    return this.fieldsService.getWeekEndingDate(year, weekNum);
  }

  generateYears(): void {
    for (let year = this.currentYear; year >= 2020; year--) {
      this.years.push(year);
    }
  }

  loadWeeksData(): void {
    this.years.forEach(year => {
      this.weeksData[year] = this.fieldsService.WeeksForYearData(year);
      if (year === this.currentYear) {
        // Slice to current week for the current year (based on week year, not calendar year)
        this.weeksData[year] = this.weeksData[year].slice(0, this.currentWeek);
      }
    });
  }

  getWeeksForYear(year: number): any[] {
    return this.weeksData[year] || [];
  }

  getCurrentWeek(): number {
    return this.fieldsService.getCurrentWeek();
  }

  isSubmitted(year: number, week: any): boolean {
    return this.submittedReports[year]?.some(report => report.field_week == week.key);
  }

  isRejected(year: number, week: any): boolean {
    return this.rejectedReports[year]?.some(report => report.field_week == week.key);
  }

  isReportSubmitted(year: number, week: any, reportType: string): boolean {
    // First check internal reports data
    const internalSubmitted = this.reports.some(report =>
      report.field_year === year.toString() &&
      report.field_week === week.key &&
      report.type === reportType &&
      report.field_submission_status === 'Submitted'
    );

    if (internalSubmitted) return true;

    // Then check AMI reports data
    const mappedType = this.mapReportType(reportType);
    const found = this.amiReports[year]?.some(report => {
      const hasWeekNumber = report.week_number && report.week_number.toString() === week.key;
      const isCorrectType = report.type === mappedType;
      return hasWeekNumber && isCorrectType;
    });

    return found || false;
  }

  mapReportType(reportType: string): string {
    // Map internal report types to AMI API report types
    switch (reportType) {
      case 'business': return 'studio_business';
      case 'staff_performance': return 'staff_performance';
      case 'royalty': return 'royalty';
      case 'miscellaneous': return 'miscellaneous';
      default: return reportType;
    }
  }

  getPreparedBy(year: number, week: any, reportType: string): string {
    // First check internal reports
    const internalReport = this.reports.find(report =>
      report.field_year === year.toString() &&
      report.field_week === week.key &&
      report.type === reportType &&
      report.field_submission_status === 'Submitted'
    );

    if (internalReport) {
      return `${internalReport.field_first_name} ${internalReport.field_last_name}`;
    }

    // Then check AMI reports
    const mappedType = this.mapReportType(reportType);
    const amiReport = this.amiReports[year]?.find(report =>
      report.week_number && report.week_number.toString() === week.key &&
      report.type === mappedType
    );

    return amiReport ? amiReport.prepared_by : '';
  }

  loadReports(): void {
    const endpoint: string = "/api_rest/v1/ami_report_submissions";
    this.drupalRESTService.httpGET(endpoint).subscribe((data: any) => {
      this.reports = data;
      this.reports.forEach(report => {
        const year = Number(report.field_year);
        const week = Number(report.field_week);
        if (report.field_submission_status === 'Submitted') {
          if (!this.submittedReports[year]) {
            this.submittedReports[year] = [];
          }
          this.submittedReports[year].push(report);
        } else if (report.field_submission_status === 'Rejected') {
          if (!this.rejectedReports[year]) {
            this.rejectedReports[year] = [];
          }
          this.rejectedReports[year].push(report);
        }
      });
    });
  }

  getAMIReports(week: string = null, year: string = null, reportType: string = null): Observable<any> {
    const endpoint: string = "/api_rest/v1/getAllAMIReports";
    const params = [];

    // Add optional parameters only if they're provided
    if (week) {
      params.push({ parameter: 'week', value: week });
    }

    if (year) {
      params.push({ parameter: 'year', value: year });
    }

    // Add reportType as a filter if specified
    if (reportType) {
      params.push({ parameter: 'type', value: reportType });
    }

    return this.drupalRESTService.httpGET(endpoint, params).pipe(
      map((response: any) => {
        if (response?.success && response?.data) {
          return response.data;
        }
        return null;
      })
    );
  }

  toggleYear(year: number): void {
    if (this.expandedYears.has(year)) {
      this.expandedYears.delete(year);
    } else {
      this.expandedYears.add(year);
      // Load AMI reports for this year if not already loaded
      if (!this.amiReports[year]) {
        this.getAMIReports(null, year.toString()).subscribe(
          data => {
            if (data && Array.isArray(data)) {
              // Process reports same way as loadAMIReports
              data.forEach(report => {
                // If report doesn't have week_number or week_year, calculate them
                if (!report.week_number || !report.week_year) {
                  if (report.date_started) {
                    const weekInfo = this.weekCalculationService.calculateWeekInfo(report.date_started);
                    report.week_number = weekInfo.weekNumber;
                    report.week_year = weekInfo.year;
                    LoggerService.log(`Calculated week info for ${report.type} report:`, weekInfo);
                  } else {
                    LoggerService.warn('Report missing both week info and date_started:', report);
                    return;
                  }
                }

                const reportYear = report.week_year;
                if (!reportYear) {
                  LoggerService.warn('Report missing week_year:', report);
                  return;
                }
                if (!this.amiReports[reportYear]) {
                  this.amiReports[reportYear] = [];
                }
                this.amiReports[reportYear].push(report);
              });
              LoggerService.log(`Loaded AMI Reports for year ${year}:`, this.amiReports[year] || 'None');
            }
          },
          error => {
            // Handle error silently - studio might not be configured for AMI
            LoggerService.log(`AMI reports not available for year ${year}`, error);
            this.amiReports[year] = [];
          }
        );
      }
    }
  }

  isYearExpanded(year: number): boolean {
    return this.expandedYears.has(year);
  }

  getWeeksForYearReversed(year: number): any[] {
    // Return a reversed copy without mutating the original array
    const weeks = this.weeksData[year] || [];
    return [...weeks].reverse();
  }

  openReportDetails(year: number, week: any, reportType: string): void {
    // Map report type
    const mappedType = this.mapReportType(reportType);

    // Check if report is submitted
    if (!this.isReportSubmitted(year, week, reportType)) {
      // Report doesn't exist - redirect to form to create it
      LoggerService.log('Report not submitted, redirecting to form for', { year, week: week.key, type: mappedType });
      this.navigateToReportForm(reportType, year, week.key);
      return;
    }

    // Find the report in already-loaded data instead of making another API call
    const report = this.amiReports[year]?.find(r =>
      r.week_number && r.week_number.toString() === week.key &&
      r.type === mappedType
    );

    if (report) {
      // Open dialog with cached report data
      this.dialog.open(ReportDetailsDialogComponent, {
        height: 'auto',
        width: '90vw',
        maxWidth: '1200px',
        data: {
          year: year,
          week: week.key,
          reportType: mappedType,
          reportData: report
        },
        panelClass: 'report-details-dialog-container'
      });
    } else {
      // Report exists but not in cache - redirect to form
      LoggerService.log('Report not in cache, redirecting to form for', { year, week: week.key, type: mappedType });
      this.navigateToReportForm(reportType, year, week.key);
    }
  }

  navigateToReportForm(reportType: string, year: number, week: string): void {
    const routes = {
      'staff_performance': '/ami-staff-performance',
      'business': '/studio-business-summary',
      'royalty': '/royalty-report',
      'miscellaneous': '/miscellaneous-report'
    };

    const route = routes[reportType];
    if (route) {
      this.router.navigate([route], {
        queryParams: {
          week: week,
          year: year,
          page: 0
        }
      });
    } else {
      LoggerService.error('Unknown report type:', reportType);
    }
  }
}
